import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type {
  DeviationRow,
  EffectivePlan,
  StandardRow,
  WeekChip,
} from "@/services/api/effectivePlan";
import { effectivePlanApi } from "@/services/api/effectivePlan";
import { TeacherLetterDirectory } from "@/components/teacher/TeacherLetterDirectory";
import type { ColleagueTeacher } from "@/services/api/teacher";
import { extractErrorMessage } from "@/utils/errorHandler";
import { ENTRY_FIELD_COLORS } from "@/components/timetable-import/entryUtils";
import { exclusionHatchStyle, exclusionTitle } from "@/utils/exclusionHatch";
import { isoParts, sameWeek, weekId, type WeekKey } from "./planFormat";
import {
  templateAxisKey,
  templateAxisLesson,
} from "@/components/timetable-import/timeUtils";
import { spanClockAxis } from "@/utils/teacherContextGrid";
import { NARROW_QUERY, planPillText, type Pane } from "./planZoom";

const SILENT_MS = 25000;
const BASE_DAYS = ["MON", "TUE", "WED", "THU", "FRI"] as const;
const DAY_LABEL: Record<string, string> = {
  MON: "Mo",
  TUE: "Di",
  WED: "Mi",
  THU: "Do",
  FRI: "Fr",
  SAT: "Sa",
  SUN: "So",
};
const MONTHS = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

function daysOf(plan: EffectivePlan): string[] {
  if (plan.template_grid?.days?.length) return plan.template_grid.days;
  return plan.show_saturday ? [...BASE_DAYS, "SAT"] : [...BASE_DAYS];
}

function monthsOf(from: string, to: string): Array<[number, number]> {
  const fromParts = from.split("-").map(Number);
  const toParts = to.split("-").map(Number);
  let y = fromParts[0] ?? 2026;
  let m = fromParts[1] ?? 8;
  const ty = toParts[0] ?? y;
  const tm = toParts[1] ?? m;
  const out: Array<[number, number]> = [];
  while (y < ty || (y === ty && m <= tm)) {
    out.push([y, m]);
    m += 1;
    if (m === 13) {
      m = 1;
      y += 1;
    }
  }
  return out;
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

interface Props {
  teacherId: string | null;
  directory?: "none" | "plan-teachers";
  caseWeeks?: WeekKey[];
  actions?: ReactNode | ((plan: EffectivePlan | null) => ReactNode);
  leading?: ReactNode;
  single?: ReactNode;
  onTeacherId?: (id: string) => void;
  onPlan?: (plan: EffectivePlan | null) => void;
  reloadKey?: number;
  plan?: EffectivePlan | null;
  readOnlyLabel?: string | null;
  live?: boolean;
  toolbarExtra?: ReactNode;
  overlay?: ReactNode;
  onWeek?: (week: WeekKey | null) => void;
}

export function EffectivePlanView({
  teacherId,
  directory = "none",
  caseWeeks,
  actions,
  leading,
  single,
  onTeacherId,
  onPlan,
  reloadKey = 0,
  plan: planProp,
  readOnlyLabel,
  live = false,
  toolbarExtra,
  overlay,
  onWeek,
}: Props) {
  const [plan, setPlan] = useState<EffectivePlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [week, setWeek] = useState<WeekKey | null>(null);
  const [teachers, setTeachers] = useState<ColleagueTeacher[]>([]);
  const [query, setQuery] = useState("");
  const onPlanRef = useRef(onPlan);
  onPlanRef.current = onPlan;
  const onWeekRef = useRef(onWeek);
  onWeekRef.current = onWeek;
  const onTeacherRef = useRef(onTeacherId);
  onTeacherRef.current = onTeacherId;
  const teacherIdRef = useRef(teacherId);
  teacherIdRef.current = teacherId;
  const planRef = useRef(plan);
  planRef.current = plan;
  const weekRef = useRef(week);
  weekRef.current = week;
  const fetchGen = useRef(0);
  const [pane, setPane] = useState<Pane>("both");
  const [narrow, setNarrow] = useState(false);

  useEffect(() => {
    if (directory !== "plan-teachers") return;
    let live = true;
    void effectivePlanApi
      .listTeachers()
      .then((res) => {
        if (!live) return;
        setTeachers(res.teachers);
        if (!teacherIdRef.current && res.teachers[0])
          onTeacherRef.current?.(res.teachers[0].id);
      })
      .catch((err) => {
        if (live)
          setError(
            extractErrorMessage(err, {
              loadError: "Lehrpersonen konnten nicht geladen werden",
            })
          );
      });
    return () => {
      live = false;
    };
  }, [directory]);

  const caseKey = caseWeeks?.map(weekId).join(",") ?? "";
  const shownPlan = planProp ?? plan;
  useEffect(() => {
    if (!caseKey) {
      setWeek(null);
      return;
    }
    if (!shownPlan) return;
    const keys = caseKey
      .split(",")
      .filter(Boolean)
      .map((id) => {
        const [year, weekNo] = id.split("-").map(Number);
        return { iso_year: year || 0, iso_week: weekNo || 0 };
      });
    setWeek(
      keys.find((key) => shownPlan.weeks.some((item) => sameWeek(item, key))) ??
        null
    );
  }, [teacherId, caseKey, shownPlan]);

  useEffect(() => {
    if (planProp) {
      setPlan(planProp);
      onPlanRef.current?.(planProp);
      return;
    }
    if (!teacherId) {
      setPlan(null);
      return;
    }
    let active = true;
    const gen = ++fetchGen.current;
    setLoading(true);
    setError(null);
    void effectivePlanApi
      .get(teacherId)
      .then((res) => {
        if (!active || gen !== fetchGen.current) return;
        setPlan(res);
        onPlanRef.current?.(res);
        setLoading(false);
      })
      .catch((err) => {
        if (!active || gen !== fetchGen.current) return;
        setPlan(null);
        onPlanRef.current?.(null);
        setError(
          extractErrorMessage(err, {
            loadError: "Stundenplan konnte nicht geladen werden",
          })
        );
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [teacherId, reloadKey, planProp]);

  useEffect(() => {
    if (!live || !teacherId || planProp) return;
    let cancelled = false;
    let timer: number | undefined;
    const pull = () => {
      if (cancelled || document.visibilityState !== "visible") return;
      const gen = ++fetchGen.current;
      void effectivePlanApi
        .get(teacherId)
        .then((res) => {
          if (cancelled || gen !== fetchGen.current) return;
          const prev = planRef.current;
          if (!prev || JSON.stringify(prev) !== JSON.stringify(res)) {
            setPlan(res);
            onPlanRef.current?.(res);
          }
          setError(null);
          setLoading(false);
          const selected = weekRef.current;
          if (selected && !res.weeks.some((w) => sameWeek(selected, w)))
            setWeek(null);
        })
        .catch((err) => {
          if (cancelled || gen !== fetchGen.current) return;
          setLoading(false);
          if (!planRef.current) {
            setError(
              extractErrorMessage(err, {
                loadError: "Stundenplan konnte nicht geladen werden",
              })
            );
          }
        });
    };
    const arm = () => {
      if (timer) window.clearInterval(timer);
      timer = undefined;
      if (document.visibilityState !== "visible") return;
      timer = window.setInterval(pull, SILENT_MS);
    };
    const onVis = () => {
      if (document.visibilityState === "visible") pull();
      arm();
    };
    document.addEventListener("visibilitychange", onVis);
    arm();
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVis);
      if (timer) window.clearInterval(timer);
    };
  }, [live, teacherId, planProp]);

  const weeks = useMemo(() => {
    if (!plan) return [];
    if (!caseWeeks?.length) return plan.weeks;
    const ids = new Set(caseWeeks.map(weekId));
    return plan.weeks.filter((w) => ids.has(weekId(w)));
  }, [plan, caseWeeks]);

  const toggle = (next: WeekKey) => {
    setWeek((prev) => (sameWeek(prev, next) ? null : next));
  };

  useEffect(() => {
    onWeekRef.current?.(week);
  }, [week]);

  const ownGrid = Boolean(plan) && !single;
  const weekOpen = Boolean(plan && week);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(NARROW_QUERY);
    const apply = () => setNarrow(mq.matches);
    apply();
    if (typeof mq.addEventListener !== "function") return;
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    if (!narrow) setPane("both");
  }, [narrow]);

  const personLabel =
    readOnlyLabel ??
    (directory === "plan-teachers" && plan
      ? `${[plan.teacher.last_name, plan.teacher.first_name].filter(Boolean).join(", ")} · nur lesen`
      : null);

  const visibleTeachers = teachers.filter((p) => {
    const t = query.trim().toLowerCase();
    if (!t) return true;
    return (
      p.last_name.toLowerCase().includes(t) ||
      p.first_name.toLowerCase().includes(t) ||
      (p.teacher_code || "").toLowerCase().includes(t)
    );
  });

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
      {error ? (
        <div className="rounded bg-red-100 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      ) : null}
      {loading ? (
        <div className="text-sm text-gray-500">Stundenplan laden…</div>
      ) : null}
      {plan ? (
        <>
          {caseWeeks ? null : (
            <MonthBand plan={plan} week={week} onPick={toggle} />
          )}
          {leading ? null : (
            <WeekChips
              weeks={weeks}
              week={week}
              deviations={plan.deviations}
              onPick={toggle}
            />
          )}
        </>
      ) : null}
      <div
        className={
          directory === "plan-teachers" || leading
            ? "grid min-h-0 flex-1 grid-cols-[auto_1fr] gap-3"
            : "flex min-h-0 min-w-0 flex-1 flex-col"
        }
      >
        {directory === "plan-teachers" ? (
          <TeacherLetterDirectory
            title="Verzeichnis"
            compact
            items={visibleTeachers}
            query={query}
            onQueryChange={setQuery}
            selectedId={teacherId}
            onSelect={(t) => {
              setWeek(null);
              onTeacherId?.(t.id);
            }}
          />
        ) : (
          leading
        )}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2 overflow-hidden">
          {personLabel && (week || directory === "plan-teachers") ? (
            <p className="text-xs text-gray-400" data-testid="plan-readonly">
              {personLabel}
            </p>
          ) : null}
          {toolbarExtra || leading || (weekOpen && narrow) ? (
            <div className="flex flex-wrap items-center gap-1.5">
              {leading && plan ? (
                <WeekChips
                  inline
                  weeks={weeks}
                  week={week}
                  deviations={plan.deviations}
                  onPick={toggle}
                />
              ) : null}
              {toolbarExtra}
              {weekOpen && narrow ? (
                <>
                  <button
                    type="button"
                    className={paneBtn(pane === "std")}
                    aria-pressed={pane === "std"}
                    onClick={() => setPane("std")}
                  >
                    Standard
                  </button>
                  <button
                    type="button"
                    className={paneBtn(pane === "eff")}
                    aria-pressed={pane === "eff"}
                    onClick={() => setPane("eff")}
                  >
                    Effektiv
                  </button>
                  <button
                    type="button"
                    className={paneBtn(pane === "both")}
                    aria-pressed={pane === "both"}
                    onClick={() => setPane("both")}
                  >
                    Beide
                  </button>
                </>
              ) : null}
            </div>
          ) : null}
          <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
            {overlay}
            {single ? (
              <div className="isolate min-h-0 flex-1 overflow-hidden">
                {single}
              </div>
            ) : weekOpen && plan && week ? (
              <div
                data-testid="plan-stage"
                className="isolate min-h-0 min-w-0 flex-1 overflow-auto outline-none"
              >
                <Twin
                  plan={plan}
                  week={week}
                  pane={narrow ? pane : "both"}
                  compact={false}
                  stacked={narrow && pane === "both"}
                />
              </div>
            ) : ownGrid && plan ? (
              <div
                data-testid="plan-stage"
                className="isolate min-h-0 min-w-0 flex-1 overflow-auto outline-none"
              >
                <StandardOnly plan={plan} compact={false} />
              </div>
            ) : null}
          </div>
          {plan && week ? <SundayNote plan={plan} week={week} /> : null}
          {typeof actions === "function" ? actions(plan) : actions}
        </div>
      </div>
    </div>
  );
}

function MonthBand({
  plan,
  week,
  onPick,
}: {
  plan: EffectivePlan;
  week: WeekKey | null;
  onPick: (w: WeekKey) => void;
}) {
  const byDate = new Map<string, DeviationRow[]>();
  for (const d of plan.deviations) {
    const list = byDate.get(d.date) ?? [];
    list.push(d);
    byDate.set(d.date, list);
  }
  return (
    <div className="flex gap-3 overflow-x-auto pb-1" data-testid="month-band">
      {monthsOf(plan.date_from, plan.date_to).map(([y, m]) => {
        const first = new Date(Date.UTC(y, m - 1, 1));
        const lead = (first.getUTCDay() + 6) % 7;
        const count = new Date(Date.UTC(y, m, 0)).getUTCDate();
        const cells: Array<number | null> = Array(lead).fill(null);
        for (let d = 1; d <= count; d++) cells.push(d);
        while (cells.length % 7) cells.push(null);
        return (
          <article
            key={`${y}-${m}`}
            className="w-56 shrink-0 rounded border border-gray-200 bg-white p-2"
          >
            <h5 className="mb-1 text-xs font-semibold text-gray-700">
              {MONTHS[m - 1]} {y}
            </h5>
            <table className="w-full text-center text-[11px]">
              <thead>
                <tr>
                  {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((d) => (
                    <th key={d} className="font-normal text-gray-400">
                      {d}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: cells.length / 7 }, (_, i) => (
                  <tr key={i}>
                    {cells.slice(i * 7, i * 7 + 7).map((d, j) => {
                      if (!d) return <td key={j} />;
                      const iso = `${y}-${pad(m)}-${pad(d)}`;
                      const hits = byDate.get(iso) ?? [];
                      if (!hits.length)
                        return (
                          <td key={j}>
                            <span className="text-gray-700">{d}</span>
                          </td>
                        );
                      const key = isoParts(iso);
                      const on = sameWeek(week, key);
                      const covered = hits.some(
                        (h) => h.sign === "minus" && !h.open
                      );
                      const plus = hits.some((h) => h.sign === "plus");
                      const hasMinus = hits.some((h) => h.sign === "minus");
                      const onlyOpen = hits.every(
                        (h) => h.sign === "minus" && h.open
                      );
                      const both = hasMinus && plus;
                      const tone = onlyOpen
                        ? "border-orange-500 bg-orange-100 text-orange-950"
                        : hasMinus && !plus
                          ? "border-red-500 bg-red-100 text-red-950"
                          : both
                            ? "border-gray-500 bg-gray-100 text-gray-900"
                            : "border-green-600 bg-green-100 text-green-950";
                      return (
                        <td key={j}>
                          <button
                            type="button"
                            className={`w-full rounded border px-0.5 leading-tight ${tone} ${on ? "ring-2 ring-gray-900" : ""}`}
                            onClick={() => onPick(key)}
                            aria-label={`KW ${key.iso_week}`}
                          >
                            <span className="block text-[9px] font-semibold">
                              {key.iso_week}
                            </span>
                            <span>{d}</span>
                            {hasMinus || plus ? (
                              <span className="block text-[9px] leading-none">
                                {hasMinus ? (
                                  <span
                                    className={
                                      covered ? "text-red-700" : "text-gray-500"
                                    }
                                  >
                                    −
                                  </span>
                                ) : null}
                                {plus ? (
                                  <span className="text-green-700">+</span>
                                ) : null}
                              </span>
                            ) : null}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </article>
        );
      })}
    </div>
  );
}

function WeekChips({
  weeks,
  week,
  deviations,
  onPick,
  inline = false,
}: {
  weeks: WeekChip[];
  week: WeekKey | null;
  deviations: DeviationRow[];
  onPick: (w: WeekKey) => void;
  inline?: boolean;
}) {
  if (!weeks.length) return null;
  return (
    <div
      className={inline ? "contents" : "flex flex-wrap gap-2"}
      data-testid="week-chips"
    >
      {inline ? (
        <style>{`@keyframes kw-pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(17,24,39,.55); } 70% { box-shadow: 0 0 0 6px rgba(17,24,39,0); } } .kw-pulse { animation: kw-pulse 1.2s ease-out infinite; }`}</style>
      ) : null}
      {weeks.map((w) => {
        const on = sameWeek(week, w);
        const rows = deviations.filter((d) => sameWeek(w, d));
        const covered = rows.some((d) => d.sign === "minus" && !d.open);
        const minusClass = on ? "" : covered ? "text-red-700" : "text-gray-500";
        const plusClass = on ? "" : "text-green-700";
        return (
          <button
            key={weekId(w)}
            type="button"
            data-testid={`chip-${weekId(w)}`}
            className={`rounded border text-left ${inline ? "px-2 py-1 text-xs font-bold" : "px-3 py-1"} ${on ? `border-gray-900 bg-gray-900 text-white ${inline ? "kw-pulse" : ""}` : "border-gray-300 bg-white"}`}
            onClick={() => onPick(w)}
          >
            {inline ? (
              <span className="whitespace-nowrap">
                KW {w.iso_week}
                {w.minus ? (
                  <span className={minusClass}> −{w.minus}</span>
                ) : null}
                {w.plus ? <span className={plusClass}> +{w.plus}</span> : null}
              </span>
            ) : (
              <>
                <span className="block text-[10px] uppercase tracking-wide">
                  KW
                </span>
                <span className="text-lg font-semibold leading-none">
                  {w.iso_week}
                </span>
                <span className="mt-1 block text-sm">
                  {w.minus ? (
                    <span className={minusClass}>−{w.minus}</span>
                  ) : null}
                  {w.minus && w.plus ? "  " : null}
                  {w.plus ? <span className={plusClass}>+{w.plus}</span> : null}
                </span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}

function lessonOnAxis(
  axis: string[],
  key: string,
  start: string,
  end: string,
  lessonNumber?: number | null
): boolean {
  const clock = key.split("#")[0] ?? key;
  if (slotKey(start, end) !== clock) return false;
  const first =
    axis.find((row) => (row.split("#")[0] ?? row) === clock) === key;
  const numbered =
    lessonNumber != null &&
    axis.some((row) => templateAxisLesson(row) === lessonNumber);
  if (lessonNumber == null || !numbered) return first;
  return templateAxisLesson(key) === lessonNumber;
}

function slotKey(start: string, end: string): string {
  return `${start}–${end || start}`;
}

function timesOf(plan: EffectivePlan, week: WeekKey | null): string[] {
  const set = new Set<string>();
  for (const row of plan.standard) set.add(slotKey(row.start, row.end));
  if (week) {
    for (const d of plan.deviations) {
      if (sameWeek(week, d)) set.add(slotKey(d.start, d.end));
    }
  }
  const occupied = [...set];
  if (plan.template_grid) {
    return (plan.template_grid.rows ?? []).map((row, index) =>
      templateAxisKey(row.label, row.lesson, index)
    );
  }
  if (!plan.slots?.length) {
    return occupied.sort(
      (a, b) => clockKey(a) - clockKey(b) || a.localeCompare(b)
    );
  }
  return [...new Set(spanClockAxis(occupied, plan.slots))];
}

function clockKey(label: string): number {
  const start = label.split("–")[0] ?? "";
  const end = label.split("–")[1] ?? "";
  if (!start) return Number.POSITIVE_INFINITY;
  const [hour = "0", minute = "0"] = start.split(":");
  const [endHour = hour, endMinute = minute] = end.split(":");
  return (
    (Number(hour) * 60 + Number(minute)) * 10000 +
    Number(endHour) * 60 +
    Number(endMinute)
  );
}

function cellTone(row: DeviationRow): string {
  if (row.sign === "plus") return "border-green-600 bg-green-100";
  return "border-red-500 bg-red-100";
}

function statusTone(row: DeviationRow): string {
  if (row.sign === "plus" || !row.open) return "bg-green-600";
  return "bg-orange-600";
}

function Raster({
  plan,
  week,
  side,
  times,
  compact,
}: {
  plan: EffectivePlan;
  week: WeekKey | null;
  side: "std" | "eff";
  times?: string[];
  compact: boolean;
}) {
  const days = daysOf(plan);
  const axis = times ?? timesOf(plan, side === "eff" ? week : null);
  const devs = plan.deviations.filter(
    (d) => week && sameWeek(week, d) && d.weekday !== "SUN"
  );
  return (
    <div
      className="w-max rounded border border-gray-200 bg-white"
      data-testid={side === "eff" ? "effective-grid" : "standard-grid"}
    >
      <div
        className="grid"
        style={{ gridTemplateColumns: `7.5rem repeat(${days.length}, 11rem)` }}
      >
        <div className="sticky top-0 z-20 border-b border-r bg-white px-2 py-1 text-xs text-gray-400">
          Zeit
        </div>
        {days.map((day) => {
          const hits = devs.filter((d) => d.weekday === day);
          const show = side === "eff" && hits.length > 0;
          return (
            <div
              key={day}
              className="sticky top-0 z-20 min-w-0 border-b border-r bg-white px-2 py-1 text-xs font-semibold"
            >
              {show ? (
                <span
                  className="mr-1 rounded bg-gray-900 px-1 text-[10px] text-white"
                  data-testid={`col-badge-${day}`}
                >
                  {week?.iso_week}
                </span>
              ) : null}
              {DAY_LABEL[day]}
            </div>
          );
        })}
        {axis.map((t) => (
          <div key={t} className="contents">
            <div className="border-b border-r px-2 py-2 text-xs text-gray-500">
              {t.split("#")[0]}
            </div>
            {days.map((day) => {
              const clock = t.split("#")[0] ?? t;
              const movedAway = new Set(
                devs
                  .filter(
                    (d) =>
                      d.weekday === day &&
                      d.entry_id &&
                      slotKey(d.start, d.end) !== clock
                  )
                  .map((d) => d.entry_id)
              );
              const sameRow = (
                start: string,
                end: string,
                lessonNumber?: number | null
              ) => lessonOnAxis(axis, t, start, end, lessonNumber);
              const base = plan.standard.filter(
                (s) =>
                  s.weekday === day &&
                  sameRow(s.start, s.end, s.lesson_number) &&
                  !(side === "eff" && movedAway.has(s.entry_id))
              );
              const hits =
                side === "eff"
                  ? devs.filter(
                      (d) => d.weekday === day && sameRow(d.start, d.end)
                    )
                  : [];
              return (
                <div key={day} className="min-w-0 border-b border-r p-1">
                  {(() => {
                    const minusIds = new Set(
                      hits
                        .filter((h) => h.sign === "minus" && h.entry_id)
                        .map((h) => h.entry_id)
                    );
                    const kept = base.filter((s) => !minusIds.has(s.entry_id));
                    return (
                      <>
                        {kept.map((s) => (
                          <StdCell key={s.entry_id} row={s} />
                        ))}
                        {hits.map((h) => (
                          <DeviationCell
                            key={h.lesson_id}
                            row={h}
                            week={week?.iso_week ?? 0}
                            compact={compact}
                          />
                        ))}
                      </>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function FieldChip({
  kind,
  text,
}: {
  kind: "subject" | "class_name" | "room";
  text: string;
}) {
  if (!text) return null;
  const tone = ENTRY_FIELD_COLORS[kind];
  return (
    <span
      className={`mr-1 mt-0.5 inline-block rounded px-1 py-0.5 ${tone.bg} ${tone.text}`}
    >
      {text}
    </span>
  );
}

function StdCell({ row }: { row: StandardRow }) {
  return (
    <div
      className="rounded border border-gray-200 px-1 py-1 text-xs"
      style={row.substitution_excluded ? exclusionHatchStyle : undefined}
      title={row.substitution_excluded ? exclusionTitle : undefined}
      data-excluded={row.substitution_excluded ? "1" : "0"}
    >
      <FieldChip kind="subject" text={row.subject} />
      <span className="block">
        <FieldChip kind="class_name" text={row.class_name} />
        <FieldChip kind="room" text={row.room} />
      </span>
    </div>
  );
}

function StatusPill({ row, compact }: { row: DeviationRow; compact: boolean }) {
  const text = planPillText(row, compact);
  return (
    <span
      className={`mt-0.5 inline-block max-w-full break-words rounded-full px-2 py-0.5 text-[11px] font-bold text-white ${statusTone(row)}`}
      data-testid={`pill-${row.lesson_id}`}
      title={planPillText(row, false)}
    >
      {text}
    </span>
  );
}

function DeviationCell({
  row,
  week,
  compact,
}: {
  row: DeviationRow;
  week: number;
  compact: boolean;
}) {
  return (
    <div
      className={`mb-1 rounded border-2 px-1 py-1 text-xs ${cellTone(row)}`}
      style={row.substitution_excluded ? exclusionHatchStyle : undefined}
      title={row.substitution_excluded ? exclusionTitle : undefined}
      data-testid={`cell-${row.lesson_id}`}
      data-open={row.open ? "1" : "0"}
      data-sign={row.sign}
      data-excluded={row.substitution_excluded ? "1" : "0"}
    >
      <span className="block text-[10px] font-semibold text-gray-800">
        {week}
      </span>
      {row.sign === "plus" ? (
        <>
          <span className="block">
            <FieldChip kind="subject" text={row.subject} />
          </span>
          <span className="block">
            <FieldChip kind="class_name" text={row.class_name} />
            <FieldChip kind="room" text={row.room} />
          </span>
        </>
      ) : null}
      <StatusPill row={row} compact={compact} />
    </div>
  );
}

function Twin({
  plan,
  week,
  pane,
  compact,
  stacked,
}: {
  plan: EffectivePlan;
  week: WeekKey;
  pane: Pane;
  compact: boolean;
  stacked: boolean;
}) {
  const times = timesOf(plan, week);
  const showStd = pane !== "eff";
  const showEff = pane !== "std";
  return (
    <div
      className={`flex w-max items-start gap-3 ${stacked ? "flex-col" : "flex-row"}`}
      data-testid="twin"
    >
      {showStd ? (
        <div>
          <h5 className="mb-1 text-xs font-semibold text-gray-500">
            Standard · jede Woche
          </h5>
          <Raster
            plan={plan}
            week={week}
            side="std"
            times={times}
            compact={compact}
          />
        </div>
      ) : null}
      {showEff ? (
        <div>
          <h5 className="mb-1 text-xs font-semibold text-gray-500">
            Effektiv · KW {week.iso_week}
          </h5>
          <Raster
            plan={plan}
            week={week}
            side="eff"
            times={times}
            compact={compact}
          />
        </div>
      ) : null}
    </div>
  );
}

function StandardOnly({
  plan,
  compact,
}: {
  plan: EffectivePlan;
  compact: boolean;
}) {
  return (
    <div data-testid="single-grid">
      <Raster plan={plan} week={null} side="std" compact={compact} />
    </div>
  );
}

function paneBtn(on: boolean): string {
  return `rounded border px-2 py-1 text-xs ${on ? "border-gray-900 bg-gray-900 text-white" : "border-gray-300 bg-white text-gray-700"}`;
}

function SundayNote({ plan, week }: { plan: EffectivePlan; week: WeekKey }) {
  const rows = plan.deviations.filter(
    (d) => d.weekday === "SUN" && sameWeek(d, week)
  );
  if (!rows.length) return null;
  return (
    <p className="text-xs text-gray-600">
      {rows
        .map(
          (r) =>
            `Sonntag ${slotKey(r.start, r.end)} · ${planPillText(r, false)}`
        )
        .join(" · ")}
    </p>
  );
}
